/* ============================================================
   Lens Configuration cart bundle
   --------------------------------------------
   Merges the 3 cart lines (frame + lens type + lens index) that
   share a `_Lens Configuration` property into one visual bundle
   card on BOTH the cart page and the minicart drawer.

   - Bundle quantity input / remove button update ALL 3 lines at
     once via /cart/update.js (multi-line `updates` payload).
   - Edit button opens a modal; saving removes the 3 old lines and
     re-adds 3 new lines (same frame variant, new lens options /
     prescription, same configuration id) via /cart/update.js +
     /cart/add.js.
   - Original lines stay in the DOM (hidden) so the cart form still
     submits all 3 lines to checkout together.

   Relies on lines carrying these data attributes (added in Liquid):
     data-lens-configuration, data-key, data-product-handle,
     data-final-price, data-line-price, data-quantity
   ============================================================ */
(function () {
  'use strict';

  if (window.__lensConfigCartLoaded) return;
  window.__lensConfigCartLoaded = true;
  window.__lensConfigCartVersion = 'v8.1';
  console.log('[lens-config-cart] loaded', window.__lensConfigCartVersion);

  var LENS_TYPE_HANDLE = 'lense-type';
  var LENS_INDEX_HANDLE = 'lense-index';
  var HIDDEN_CLASS = 'lens-config-hidden';
  var BUNDLE_SEL = '.lens-config-bundle[data-lens-configuration]';
  var CART_SCOPE_SEL =
    '.cart-items tbody tr.cart-item.lens-config-item[data-lens-configuration]';
  var MINI_SCOPE_SEL =
    '#minicart-form .cart-item[data-lens-configuration]';

  var cartDataCache = {};
  var productsCache = {};
  var currentGroups = { cart: {}, minicart: {} };
  var activeConfigId = null;
  var modal = null;
  var rebuildTimer = null;
  var isRebuilding = false;
  var lastRebuildTime = 0;

  /* ----------------------------------------------------------
     Helpers
     ---------------------------------------------------------- */
  function formatPrice(cents) {
    var currency =
      (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format((cents || 0) / 100);
    } catch (e) {
      return '$' + ((cents || 0) / 100).toFixed(2);
    }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[c];
    });
  }

  function isFrameRow(r) {
    return r.handle !== LENS_TYPE_HANDLE && r.handle !== LENS_INDEX_HANDLE;
  }

  function isTypeRow(r) {
    return r.handle === LENS_TYPE_HANDLE;
  }

  function isIndexRow(r) {
    return r.handle === LENS_INDEX_HANDLE;
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!res.ok) {
            throw new Error(
              (data && (data.description || data.message)) ||
                'Request failed (' + res.status + ')'
            );
          }
          return data;
        });
    });
  }

  function getSectionIds() {
    var ids = [];
    var main = document.getElementById('main-cart-items');
    if (main && main.dataset.id) ids.push(main.dataset.id);
    if (document.getElementById('minicart-form') ||
          document.getElementById('shopify-section-minicart-form')) ids.push('minicart-form');
    return ids;
  }

  function showToast(message) {
    if (typeof window.showToast === 'function') {
      window.showToast(message, 5000, 'modal-error');
    } else if (window.alert) {
      window.alert(message);
    }
  }

  /* ----------------------------------------------------------
     Cart data cache (properties live here: prescription etc.)
     ---------------------------------------------------------- */
  function refreshCacheFromItems(items) {
    if (!items || !Array.isArray(items)) return;
    cartDataCache = {};
    items.forEach(function (it) {
      var n = {};
      Object.keys(it).forEach(function (k) {
        n[k] = it[k];
      });
      // /cart/add responses return `price` as a dollar string ("29.95")
      // with NO `final_price`, while /cart.js returns cents (2995).
      // Normalize so every consumer sees cents in `final_price`.
      if (n.final_price == null && n.price != null) {
        var p = Number(n.price);
        if (!isNaN(p)) {
          n.final_price =
            String(n.price).indexOf('.') !== -1 && Math.abs(p) < 1000
              ? Math.round(p * 100)
              : p;
        }
      }
      cartDataCache[n.key || it.key] = n;
    });
  }

  function loadCartData() {
    return fetchJSON('/cart.js')
      .then(function (cart) {
        refreshCacheFromItems(cart.items);
      })
      .catch(function () {
        /* non-fatal */
      });
  }

  function loadProduct(handle) {
    if (Object.prototype.hasOwnProperty.call(productsCache, handle)) {
      return Promise.resolve(productsCache[handle]);
    }

    return fetchJSON('/products/' + handle + '.js')
      .then(function (p) {
        productsCache[handle] = p;
        return p;
      })
      .catch(function (err) {
        productsCache[handle] = null;
        throw err;
      });
  }

  /* ----------------------------------------------------------
     Collect & group lines
     ---------------------------------------------------------- */
  function makeRow(el, scope) {
    return {
      el: el,
      scope: scope,
      key: el.dataset.key || '',
      handle: el.dataset.productHandle || '',
      configId: el.dataset.lensConfiguration || '',
      finalPrice: Number(el.dataset.finalPrice || 0),
      linePrice: Number(el.dataset.linePrice || 0),
      quantity: Number(el.dataset.quantity || 1)
    };
  }

  function collectRows() {
    var rows = [];
    document.querySelectorAll(CART_SCOPE_SEL).forEach(function (el) {
      rows.push(makeRow(el, 'cart'));
    });
    document.querySelectorAll(MINI_SCOPE_SEL).forEach(function (el) {
      rows.push(makeRow(el, 'minicart'));
    });
    return rows;
  }

  function groupRows(rows) {
    var groups = {};
    rows.forEach(function (r) {
      if (!r.configId) return;
      if (!groups[r.configId]) groups[r.configId] = [];
      groups[r.configId].push(r);
    });
    return groups;
  }

  function uniqueKeys(configId) {
    var keys = [];
    var seen = {};
    (currentGroups.cart[configId] || [])
      .concat(currentGroups.minicart[configId] || [])
      .forEach(function (r) {
        if (r.key && !seen[r.key]) {
          seen[r.key] = true;
          keys.push(r.key);
        }
      });
    return keys;
  }

  /* ----------------------------------------------------------
     Specs (refractive index / lens type / prescription)
     ---------------------------------------------------------- */
  function cleanVariantTitle(title) {
    if (!title) return '';
    var idx = title.indexOf(' - ');
    return idx >= 0 ? title.slice(idx + 3) : title;
  }

  function buildSpecLines(group) {
    var lines = [];
    var type = group.find(isTypeRow);
    var index = group.find(isIndexRow);
    var frame = group.find(isFrameRow);
    var typeData = type && cartDataCache[type.key];
    var indexData = index && cartDataCache[index.key];
    var frameData = frame && cartDataCache[frame.key];

    if (indexData && indexData.title) {
      lines.push({
        label: 'Refractive Index',
        value: cleanVariantTitle(indexData.title)
      });
    }
    if (typeData && typeData.title) {
      lines.push({
        label: 'Lens Type',
        value: cleanVariantTitle(typeData.title)
      });
    }
    if (frameData && frameData.properties) {
      var p = frameData.properties;
      [
        {
          label: 'Right Eye',
          sph: p['Prescription - Right SPH'],
          cyl: p['Prescription - Right CYL'],
          axis: p['Prescription - Right AXIS']
        },
        {
          label: 'Left Eye',
          sph: p['Prescription - Left SPH'],
          cyl: p['Prescription - Left CYL'],
          axis: p['Prescription - Left AXIS']
        }
      ].forEach(function (eye) {
        if (eye.sph || eye.cyl || eye.axis) {
          lines.push({
            label: eye.label,
            value:
              'SPH ' + (eye.sph || '-') +
              ' / CYL ' + (eye.cyl || '-') +
              ' / AXIS ' + (eye.axis || '-')
          });
        }
      });
      if (p['PD Type']) {
        if (p['PD Type'] === 'dual') {
          lines.push({
            label: 'PD',
            value:
              'Dual (Right ' + (p['Right PD'] || '-') +
              ' / Left ' + (p['Left PD'] || '-') + ')'
          });
        } else {
          lines.push({
            label: 'PD',
            value: 'Single ' + (p['PD'] || '-')
          });
        }
      }
    }
    return lines;
  }

  function specsHTML(lines) {
    return lines
      .map(function (l) {
        return (
          '<li><span class="lens-config-bundle__spec-label">' +
          esc(l.label) +
          ':</span> ' +
          esc(l.value) +
          '</li>'
        );
      })
      .join('');
  }

  /* ----------------------------------------------------------
     Bundle builders
     ---------------------------------------------------------- */
  function getFrameImgHTML(frame) {
    var img = frame && frame.el ? frame.el.querySelector('img') : null;
    if (!img) return '';
    var src = img.currentSrc || img.getAttribute('src') || img.dataset.src || '';
    if (!src) return '';
    return '<img src="' + esc(src) + '" alt="" loading="lazy">';
  }

  function bundleData(group) {
    var frame = group.find(isFrameRow) || group[0];
    var frameData = cartDataCache[frame.key];
    var unitTotal = group.reduce(function (s, r) {
      return s + r.finalPrice;
    }, 0);
    var qty = frame.quantity || 1;
    var title =
      (frameData && frameData.product_title) ||
      (frameData && frameData.title) ||
      'Glasses';
    return {
      frame: frame,
      frameData: frameData,
      unitTotal: unitTotal,
      qty: qty,
      total: unitTotal * qty,
      title: title,
      specs: buildSpecLines(group)
    };
  }

  function qtyControlHTML(qty) {
    return (
      '<div class="lens-config-bundle__qty-inner">' +
      '<button type="button" class="lens-bundle-qty__minus" aria-label="Decrease quantity">&minus;</button>' +
      '<input type="number" class="lens-bundle-qty__input" value="' +
      qty +
      '" min="0" inputmode="numeric" aria-label="Quantity">' +
      '<button type="button" class="lens-bundle-qty__plus" aria-label="Increase quantity">+</button>' +
      '</div>'
    );
  }

  function buildCartBundle(group, configId) {
    var d = bundleData(group);
    var tr = document.createElement('tr');
    tr.className = 'lens-config-bundle';
    tr.dataset.lensConfiguration = configId;
    tr.innerHTML =
      '<td colspan="5">' +
      '<div class="lens-config-bundle__card">' +
      '<div class="lens-config-bundle__media">' + getFrameImgHTML(d.frame) + '</div>' +
      '<div class="lens-config-bundle__info">' +
      '<h3 class="lens-config-bundle__title">' + esc(d.title) + '</h3>' +
      '<ul class="lens-config-bundle__specs">' + specsHTML(d.specs) + '</ul>' +
      '<button type="button" class="lens-config-bundle__edit" data-action="edit">Edit Configuration</button>' +
      '</div>' +
      '<div class="lens-config-bundle__price">' +
      '<span class="lens-config-bundle__price-label">Price</span>' +
      '<strong>' + formatPrice(d.unitTotal) + '</strong>' +
      '</div>' +
      '<div class="lens-config-bundle__qty">' + qtyControlHTML(d.qty) + '</div>' +
      '<div class="lens-config-bundle__total">' +
      '<span class="lens-config-bundle__total-label">Total</span>' +
      '<strong>' + formatPrice(d.total) + '</strong>' +
      '</div>' +
      '<button type="button" class="lens-config-bundle__remove-btn" data-action="remove" aria-label="Remove">&#10005;</button>' +
      '</div>' +
      '</td>';
    return tr;
  }

  function buildMiniBundle(group, configId) {
    var d = bundleData(group);
    var div = document.createElement('div');
    div.className = 'lens-config-bundle lens-config-bundle--minicart';
    div.dataset.lensConfiguration = configId;

    div.innerHTML =
      '<div class="lens-config-bundle__main">' +
        '<div class="lens-config-bundle__media">' + getFrameImgHTML(d.frame) + '</div>' +
        '<div class="lens-config-bundle__info">' +
          '<h3 class="lens-config-bundle__title">' + esc(d.title) + '</h3>' +
          '<div class="lens-config-bundle__color">Color: Tortoise</div>' +
          '<div class="prescription-badge">✓ Prescription</div>' +
        '</div>' +
      '</div>' +
      '<div class="lens-config-bundle__meta">' +
        '<strong>' + formatPrice(d.total) + '</strong>' +
        '<span>Qty: 1</span>' +
      '</div>' +
      '<button type="button" class="lens-details-toggle" aria-expanded="false">⌄ View Details</button>' +
      '<div class="lens-details-content">' +
        buildPrescriptionDetailHTML(d) +
        '<button type="button" class="lens-details-edit" data-action="edit">Edit Lens Options</button>' +
      '</div>' +
      '<button type="button" class="lens-config-bundle__remove-btn" data-action="remove" aria-label="Remove">&#10005;</button>';

    var toggle = div.querySelector('.lens-details-toggle');
    var content = div.querySelector('.lens-details-content');

    if (toggle && content) {
      toggle.addEventListener('pointerup', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var open = content.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.textContent = open ? '⌃ Hide Details' : '⌄ View Details';
      });
    }

    return div;
  }

  function buildPrescriptionDetailHTML(d) {
    return '<div class="lens-detail-panel">' +
      '<div class="lens-detail-header">' +
        '<strong>Prescription</strong>' +
        '<span>Single Vision</span>' +
      '</div>' +
      '<div class="lens-detail-simple">' +
        (d.specs || []).map(function (x) {
          return '<div><strong>' + esc(x.label) + '</strong><span>' + esc(x.value) + '</span></div>';
        }).join('') +
      '</div>' +
    '</div>';
  }

  /* ----------------------------------------------------------
     Update (quantity / remove) via /cart/update.js multi-line
     ---------------------------------------------------------- */
  function setBusy(configId, busy) {
    document.querySelectorAll(BUNDLE_SEL).forEach(function (el) {
      if (el.dataset.lensConfiguration === configId) {
        el.classList.toggle('is-loading', busy);
      }
    });
  }

  function updateGroup(configId, qty, inputEl, prevValue) {
    var keys = uniqueKeys(configId);
    if (!keys.length) return;
    qty = Math.max(0, parseInt(qty, 10) || 0);
    var updates = {};
    keys.forEach(function (k) {
      updates[k] = qty;
    });
    setBusy(configId, true);
    fetchJSON(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        updates: updates,
        sections: getSectionIds(),
        sections_url: window.location.pathname
      })
    })
      .then(function (state) {
        refreshCacheFromItems(state.items);
        applyCartState(state);
      })
      .catch(function (e) {
        showToast(e.message);
        if (inputEl) inputEl.value = prevValue != null ? prevValue : String(qty);
      })
      .finally(function () {
        setBusy(configId, false);
      });
  }

  /* ----------------------------------------------------------
     Apply AJAX state to the DOM (cart page + minicart)
     ---------------------------------------------------------- */
function applyCartState(state) {

  /* ----------------------------------------------------------
     1. Cart count
     ---------------------------------------------------------- */
  if (state && state.item_count != undefined) {

    document.querySelectorAll('.cart-count').forEach(function (el) {

      if (el.classList.contains('cart-count-drawer')) {

        el.innerHTML = '(' + state.item_count + ')';

      } else {

        el.innerHTML = state.item_count > 100 ? '~' : state.item_count;

      }

    });

  }


  /* ----------------------------------------------------------
     2. Header total
     ---------------------------------------------------------- */
  var headerTotal = document.querySelector('header-total-price');

  if (
    headerTotal &&
    typeof headerTotal.updateTotal === 'function'
  ) {

    try {

      headerTotal.updateTotal(state);

    } catch (e) {}

  }


  /* ----------------------------------------------------------
     3. Replace returned sections
     ---------------------------------------------------------- */
  if (state && state.sections) {

    var parser = new DOMParser();


    /* --------------------------
       Main cart
       -------------------------- */
    var main = document.getElementById('main-cart-items');

    if (
      main &&
      main.dataset.id &&
      state.sections[main.dataset.id]
    ) {

      var doc = parser.parseFromString(
        state.sections[main.dataset.id],
        'text/html'
      );

      var contents =
        document.querySelector('#main-cart-items .js-contents');

      var newContents =
        doc.querySelector('.js-contents');

      if (contents && newContents) {

        contents.innerHTML = newContents.innerHTML;

      }


      var totalsEl =
        document.querySelector('.cart-info .totals');

      var newTotals =
        doc.querySelector('.cart-info .totals');

      if (totalsEl && newTotals) {

        totalsEl.innerHTML = newTotals.innerHTML;

      }


      var giftEl =
        document.getElementById('gift');

      var newGift =
        doc.getElementById('gift');

      if (giftEl && newGift) {

        giftEl.innerHTML = newGift.innerHTML;

      }

    }


    /* --------------------------
       Mini cart
       -------------------------- */
    var miniForm =
      document.getElementById('minicart-form');

    if (
      miniForm &&
      state.sections['minicart-form']
    ) {

      var miniDoc = parser.parseFromString(
        state.sections['minicart-form'],
        'text/html'
      );

      var newMiniForm =
        miniDoc.getElementById('minicart-form') ||
        miniDoc.getElementById(
          'shopify-section-minicart-form'
        );

      if (miniForm && newMiniForm) {

        miniForm.innerHTML =
          newMiniForm.innerHTML;


        var notif =
          document.querySelector(
            'cart-notification'
          );

        if (notif) {

          if (
            typeof notif.cartAction ===
            'function'
          ) {

            try {

              notif.cartAction();

            } catch (e) {}

          }


          if (
            typeof notif.addonsUpdate ===
            'function'
          ) {

            try {

              notif.addonsUpdate();

            } catch (e) {}

          }

        }

      }

    }

  }


  /* ----------------------------------------------------------
     4. Refresh real cart data
     IMPORTANT:
     /cart/add.js is not a complete cart object.
     Always fetch /cart.js after DOM replacement so the
     free-shipping progress bar receives the real subtotal.
     ---------------------------------------------------------- */
  fetchJSON('/cart.js')

    .then(function (cart) {

      if (!cart) return;


      /* Keep local cache in sync */
      if (cart.items) {

        refreshCacheFromItems(cart.items);

      }


      /* --------------------------
         Free Shipping Progress
         -------------------------- */
      var freeShip =
        document.querySelector(
          'free-ship-progress-bar'
        );

      if (
        freeShip &&
        typeof freeShip.init === 'function'
      ) {

        var subtotal = 0;

        if (
          cart.items_subtotal_price != null
        ) {

          subtotal =
            Number(
              cart.items_subtotal_price
            );

        } else if (
          cart.total_price != null
        ) {

          subtotal =
            Number(
              cart.total_price
            );

        }


        if (isNaN(subtotal)) {

          subtotal = 0;

        }


        try {

          /*
           * Keep DOM attribute synchronized too.
           * Useful for debugging and for theme code
           * that reads data-order later.
           */
          freeShip.dataset.order =
            String(subtotal);

          freeShip.init(subtotal);

        } catch (e) {

          console.warn(
            '[lens-config-cart] free shipping update failed',
            e
          );

        }

      }


      /* --------------------------
         Header total second sync
         Some add-cart responses are incomplete,
         while /cart.js is authoritative.
         -------------------------- */
      var latestHeaderTotal =
        document.querySelector(
          'header-total-price'
        );

      if (
        latestHeaderTotal &&
        typeof latestHeaderTotal.updateTotal ===
          'function'
      ) {

        try {

          latestHeaderTotal.updateTotal(cart);

        } catch (e) {}

      }

    })

    .catch(function (e) {

      console.warn(
        '[lens-config-cart] cart refresh after applyCartState failed',
        e
      );

    });

}

  /* ----------------------------------------------------------
     Rebuild (called on init + every cart DOM change)
     ---------------------------------------------------------- */
  var lastSignature = '';
  var cacheRefreshing = false;

  function rowSignature(rows) {
    return rows
      .map(function (r) {
        return [r.configId, r.scope, r.handle, r.key, r.quantity, r.finalPrice].join(
          '|'
        );
      })
      .join(';');
  }

  function cardSignature() {
    return Array.from(document.querySelectorAll(BUNDLE_SEL))
      .map(function (c) {
        var q = c.querySelector('.lens-bundle-qty__input');
        return c.dataset.lensConfiguration + ':' + (q ? q.value : '');
      })
      .join(';');
  }

  // V12.3 Shared cart state handler for Frame Only flow
window.__lensConfigCartApplyState = applyCartState;
console.log('V12.4 ACTIVE - shared cart handler loaded');
function rebuild() {
    if (isRebuilding) return;

    if (Date.now() - lastRebuildTime < 300) {
      return;
    }

    lastRebuildTime = Date.now();
    isRebuilding = true;
    try {
      // Signature check: if nothing actually changed, do NOT touch the DOM.
      // This prevents an infinite rebuild loop caused by our own inserts
      // triggering the MutationObserver again (which froze the page / made
      // the +/- and edit buttons unresponsive).
      var rows = collectRows();
      var sig = rowSignature(rows) + '||' + cardSignature();
      if (sig === lastSignature) {
        return;
      }
      // If the cart changed but we have no cached line data yet (e.g. the
      // page loaded with an empty cart and items were added afterwards),
      // refresh the cache once, then rebuild with full data (title, specs).
      var missing = rows.some(function (r) {
        return r.key && !cartDataCache[r.key];
      });
      if (missing && !cacheRefreshing) {
        cacheRefreshing = true;
        loadCartData().then(function () {
          cacheRefreshing = false;
          lastSignature = ''; // force a full rebuild with fresh cache
          rebuild();
        });
        return;
      }
      lastSignature = sig;

      document.querySelectorAll(BUNDLE_SEL).forEach(function (el) {
        el.remove();
      });
      document.querySelectorAll('.' + HIDDEN_CLASS).forEach(function (el) {
        el.classList.remove(HIDDEN_CLASS);
      });

      currentGroups = {
        cart: groupRows(
          rows.filter(function (r) {
            return r.scope === 'cart';
          })
        ),
        minicart: groupRows(
          rows.filter(function (r) {
            return r.scope === 'minicart';
          })
        )
      };

      var tbody = document.querySelector('.cart-items tbody');
      if (tbody) {
        var cartIds = Object.keys(currentGroups.cart);
        if (cartIds.length) {
          var frag = document.createDocumentFragment();
          cartIds.forEach(function (cid) {
            frag.appendChild(buildCartBundle(currentGroups.cart[cid], cid));
          });
          tbody.insertBefore(frag, tbody.firstChild);
        }
      }

      var itemsEl = document.querySelector('#minicart-form .items');
      if (itemsEl) {
        var miniIds = Object.keys(currentGroups.minicart);
        if (miniIds.length) {
          var frag2 = document.createDocumentFragment();
          miniIds.forEach(function (cid) {
            frag2.appendChild(buildMiniBundle(currentGroups.minicart[cid], cid));
          });
          itemsEl.insertBefore(frag2, itemsEl.firstChild);
        }
      }

      rows.forEach(function (r) {
        // Only hide original rows that belong to a Lens Configuration bundle.
        // Frame Only products have no _Lens Configuration and must remain visible.
        if (r.configId) {
          r.el.classList.add(HIDDEN_CLASS);
        }
      });
    } finally {
      isRebuilding = false;
    }
  }

  
  /* ----------------------------------------------------------
     V12.0 Frame Only Mini Cart Remove Fix
     Intercept normal Frame Only cart item removal.
     Lens Bundle removal logic remains unchanged.
     ---------------------------------------------------------- */
  function setupFrameOnlyRemove() {
    document.addEventListener(
      'click',
      function (e) {
        var removeButton = e.target.closest('.minicart__item-remove');

        if (!removeButton) return;

        var item = removeButton.closest('.cart-item');

        if (!item) return;

        // Lens bundle items use existing bundle remove logic.
        if (item.dataset.lensConfiguration) return;

        var key = item.dataset.key;

        if (!key) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        fetchJSON(window.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            updates: {
              [key]: 0
            },
            sections: getSectionIds(),
            sections_url: window.location.pathname
          })
        })
        .then(function (state) {
          refreshCacheFromItems(state.items || []);
          applyCartState(state);
        })
        .catch(function (err) {
          console.error('Frame Only remove error:', err);
        });

      },
      true
    );
  }

function setupLensDetailsToggle() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.lens-details-toggle');
      if (!btn) return;
      var content = btn.nextElementSibling;
      if (!content) return;
      var open = content.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.textContent = open ? '⌃ Hide Details' : '⌄ View Details';
    });
  }

  function setupGlobalDelegation() {
    // click: bundle buttons (+ / - / edit / remove)
    document.addEventListener(
      'click',
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        var card = t.closest(BUNDLE_SEL);
        if (!card) return;
        var configId = card.dataset.lensConfiguration;
        var input = card.querySelector('.lens-bundle-qty__input');
        if (t.closest('.lens-bundle-qty__minus')) {
          var v = Math.max(0, (parseInt(input.value, 10) || 1) - 1);
          input.value = v;
          updateGroup(configId, v, input, String(v + 1));
        } else if (t.closest('.lens-bundle-qty__plus')) {
          var v = (parseInt(input.value, 10) || 1) + 1;
          input.value = v;
          updateGroup(configId, v, input, String(v - 1));
        } else if (t.closest('[data-action="edit"]')) {
          openEditModal(configId);
        } else if (t.closest('[data-action="remove"]')) {
          updateGroup(configId, 0);
        }
        if (e.stopPropagation) e.stopPropagation();
      },
      true
    );

    // change: bundle quantity input (debounced)
    document.addEventListener(
      'change',
      function (e) {
        var t = e.target;
        if (!t || !t.classList || !t.classList.contains('lens-bundle-qty__input')) {
          return;
        }
        var card = t.closest(BUNDLE_SEL);
        if (!card) return;
        // stop propagation so the theme's CartItems handler does not fire
        // a second /cart/change request for bundle lines
        if (e.stopPropagation) e.stopPropagation();
        var input = t;
        var configId = card.dataset.lensConfiguration;
        if (input.__debounceTimer) clearTimeout(input.__debounceTimer);
        input.__debounceTimer = setTimeout(function () {
          var val = input.value.trim();
          if (val === '') {
            input.value = input.dataset.prev || '1';
            return;
          }
          var prev = input.dataset.prev != null ? input.dataset.prev : input.value;
          input.dataset.prev = input.value;
          updateGroup(configId, parseInt(val, 10) || 0, input, prev);
        }, 350);
      },
      true
    );

    // keydown: prevent Enter submit inside bundle qty input
    document.addEventListener(
      'keydown',
      function (e) {
        var t = e.target;
        if (t && t.classList && t.classList.contains('lens-bundle-qty__input') && e.key === 'Enter') {
          e.preventDefault();
        }
      },
      true
    );
  }

  function scheduleRebuild() {
    if (isRebuilding) return;
    if (rebuildTimer) clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(rebuild, 50);
  }

  function reopenMiniCart() {
    var notif = document.querySelector('cart-notification');

    if (
      notif &&
      typeof notif.cartAction === 'function'
    ) {
      notif.cartAction();
    }
  }
  /* ----------------------------------------------------------
     Edit modal
     ---------------------------------------------------------- */
  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'lens-config-modal';
    modal.id = 'lens-config-modal';
    modal.innerHTML =
      '<div class="lens-config-modal__overlay"></div>' +
      '<div class="lens-config-modal__dialog" role="dialog" aria-modal="true" aria-label="Edit Configuration">' +
      '<button type="button" class="lens-config-modal__close" aria-label="Close">&times;</button>' +
      '<h3 class="lens-config-modal__title">Edit Configuration</h3>' +
      '<div class="lens-config-modal__frame"></div>' +
      '<form id="lens-config-edit-form" novalidate>' +
      '<fieldset><legend>Refractive Index</legend>' +
      '<div class="lens-config-modal__options-grid" id="lens-edit-index"></div></fieldset>' +
      '<fieldset><legend>Lens Type</legend>' +
      '<div class="lens-config-modal__options-grid" id="lens-edit-type"></div></fieldset>' +
      '<fieldset><legend>Prescription</legend>' +
      '<table class="lens-config-modal__rx-table">' +
      '<thead><tr><th></th><th>Right Eye (OD)</th><th>Left Eye (OS)</th></tr></thead>' +
      '<tbody>' +
      '<tr><th>SPH</th><td><select name="lens_edit_right_sph" required></select></td><td><select name="lens_edit_left_sph" required></select></td></tr>' +
      '<tr><th>CYL</th><td><select name="lens_edit_right_cyl" required></select></td><td><select name="lens_edit_left_cyl" required></select></td></tr>' +
      '<tr><th>AXIS</th><td><select name="lens_edit_right_axis" required></select></td><td><select name="lens_edit_left_axis" required></select></td></tr>' +
      '</tbody></table>' +
      '<div class="lens-config-modal__pd">' +
      '<div class="lens-config-modal__pd-type">' +
      '<label><input type="radio" name="lens_edit_pd_type" value="single" checked> Single PD</label>' +
      '<label><input type="radio" name="lens_edit_pd_type" value="dual"> Dual PD</label>' +
      '</div>' +
      '<div class="lens-config-modal__pd-fields" id="lens-edit-pd-single">' +
      '<label>Single PD <input type="number" name="lens_edit_pd" min="20" max="45" step="0.5" placeholder="e.g. 63"></label>' +
      '</div>' +
      '<div class="lens-config-modal__pd-fields is-hidden" id="lens-edit-pd-dual">' +
      '<label>Right PD <input type="number" name="lens_edit_right_pd" min="10" max="45" step="0.5" placeholder="e.g. 31.5"></label>' +
      '<label>Left PD <input type="number" name="lens_edit_left_pd" min="10" max="45" step="0.5" placeholder="e.g. 31.5"></label>' +
      '</div>' +
      '</div>' +
      '</fieldset>' +
      '<div class="lens-config-modal__total"><span>Total</span><strong id="lens-edit-total"></strong></div>' +
      '<div class="lens-config-modal__actions">' +
      '<button type="button" class="lens-config-modal__cancel">Cancel</button>' +
      '<button type="submit" class="lens-config-modal__save">Save Changes</button>' +
      '</div>' +
      '<div class="lens-config-modal__error" id="lens-edit-error"></div>' +
      '</form>' +
      '</div>';
    document.body.appendChild(modal);

  var close = function () {
  modal.classList.remove('is-open');
  activeConfigId = null;
  reopenMiniCart();
};
    modal.querySelector('.lens-config-modal__close').addEventListener('click', close);
    modal.querySelector('.lens-config-modal__cancel').addEventListener('click', close);
    modal.querySelector('.lens-config-modal__overlay').addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
    modal.querySelectorAll('input[name="lens_edit_pd_type"]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        togglePdFields(this.value);
      });
    });
    modal.querySelector('#lens-config-edit-form').addEventListener('submit', function (e) {
      e.preventDefault();
      saveEdit();
    });
    return modal;
  }

  function togglePdFields(pdType) {
    var single = modal.querySelector('#lens-edit-pd-single');
    var dual = modal.querySelector('#lens-edit-pd-dual');
    if (!single || !dual) return;
    single.classList.toggle('is-hidden', pdType !== 'single');
    dual.classList.toggle('is-hidden', pdType !== 'dual');
  }

  function showModalError(msg) {
    var err = modal.querySelector('#lens-edit-error');
    err.textContent = msg;
    err.classList.add('is-visible');
  }

  function generateOptions(start, end, step) {
    var out = [];
    var v = start;
    while (v <= end + 0.0001) {
      out.push(Number(v.toFixed(2)));
      v = Math.round((v + step) * 100) / 100;
    }
    return out;
  }

  function fillSelect(name, values) {
    var sel = modal.querySelector('[name="' + name + '"]');
    if (!sel || sel.dataset.filled) return;
    var frag = document.createDocumentFragment();
    var first = document.createElement('option');
    first.value = '';
    first.textContent = 'Select';
    frag.appendChild(first);
    values.forEach(function (v) {
      var o = document.createElement('option');
      o.value = String(v);
      o.textContent = String(v);
      frag.appendChild(o);
    });
    sel.appendChild(frag);
    sel.dataset.filled = '1';
  }

  function buildRxSelects() {
    fillSelect('lens_edit_right_sph', generateOptions(-10, 6, 0.25));
    fillSelect('lens_edit_left_sph', generateOptions(-10, 6, 0.25));
    fillSelect('lens_edit_right_cyl', generateOptions(-6, 6, 0.25));
    fillSelect('lens_edit_left_cyl', generateOptions(-6, 6, 0.25));
    var axisVals = [];
    for (var i = 1; i <= 180; i++) axisVals.push(i);
    fillSelect('lens_edit_right_axis', axisVals);
    fillSelect('lens_edit_left_axis', axisVals);
  }

  function renderOptionGrid(containerId, variants, name) {
    var container = modal.querySelector('#' + containerId);
    if (!container || container.dataset.filled) return;
    var frag = document.createDocumentFragment();
    variants.forEach(function (v) {
      var label = document.createElement('label');
      label.className = 'lens-config-modal__option';
      label.innerHTML =
        '<input type="radio" name="' + name + '" value="' + v.id +
        '" data-title="' + esc(v.title) + '" data-price="' + v.price + '">' +
        '<span><strong>' + esc(v.title) + '</strong><small>' +
        formatPrice(Number(v.price)) + '</small></span>';
      label.querySelector('input').addEventListener('change', function () {
        updateModalTotal();
      });
      frag.appendChild(label);
    });
    container.appendChild(frag);
    container.dataset.filled = '1';
  }

  function setRadioChecked(container, name, value) {
    var radio = container.querySelector('input[name="' + name + '"][value="' + value + '"]');
    if (radio) radio.checked = true;
  }

  function setSelectValue(name, value) {
    var sel = modal.querySelector('[name="' + name + '"]');
    if (!sel || value === undefined || value === null || value === '') return;
    var normalized = String(Number(value));
    // try exact match first, then numeric-normalized match ('-9.50' -> '-9.5')
    var opt = sel.querySelector('option[value="' + String(value) + '"]') ||
      (isNaN(Number(value)) ? null : sel.querySelector('option[value="' + normalized + '"]'));
    if (opt) sel.value = opt.value;
  }

  function updateModalTotal() {
    if (!modal) return;
    var all = (currentGroups.cart[activeConfigId] || [])
      .concat(currentGroups.minicart[activeConfigId] || []);
    var frame = all.find(isFrameRow);
    var frameData = frame && cartDataCache[frame.key];
    var framePrice = frameData ? Number(frameData.final_price || frameData.price || 0) : 0;
    var typeRadio = modal.querySelector('input[name="lens_edit_type"]:checked');
    var indexRadio = modal.querySelector('input[name="lens_edit_index"]:checked');
    var typePrice = typeRadio ? Number(typeRadio.dataset.price || 0) : 0;
    var indexPrice = indexRadio ? Number(indexRadio.dataset.price || 0) : 0;
    var totalEl = modal.querySelector('#lens-edit-total');
    if (totalEl) totalEl.textContent = formatPrice(framePrice + typePrice + indexPrice);
  }

  function fillModalFromCache(configId) {
    var all = (currentGroups.cart[configId] || [])
      .concat(currentGroups.minicart[configId] || []);
    var frame = all.find(isFrameRow);
    var type = all.find(isTypeRow);
    var index = all.find(isIndexRow);
    var frameData = frame && cartDataCache[frame.key];
    var typeData = type && cartDataCache[type.key];
    var indexData = index && cartDataCache[index.key];

    var titleEl = modal.querySelector('.lens-config-modal__frame');
    titleEl.textContent = frameData
      ? frameData.product_title || frameData.title || ''
      : '';

    if (typeData) setRadioChecked(modal, 'lens_edit_type', String(typeData.variant_id));
    if (indexData) setRadioChecked(modal, 'lens_edit_index', String(indexData.variant_id));

    var p = (frameData && frameData.properties) || {};
    setSelectValue('lens_edit_right_sph', p['Prescription - Right SPH']);
    setSelectValue('lens_edit_right_cyl', p['Prescription - Right CYL']);
    setSelectValue('lens_edit_right_axis', p['Prescription - Right AXIS']);
    setSelectValue('lens_edit_left_sph', p['Prescription - Left SPH']);
    setSelectValue('lens_edit_left_cyl', p['Prescription - Left CYL']);
    setSelectValue('lens_edit_left_axis', p['Prescription - Left AXIS']);

    var pdType = p['PD Type'] === 'dual' ? 'dual' : 'single';
    setRadioChecked(modal, 'lens_edit_pd_type', pdType);
    togglePdFields(pdType);
    var pdInput = modal.querySelector('[name="lens_edit_pd"]');
    if (pdInput) pdInput.value = p['PD'] || '';
    var rpd = modal.querySelector('[name="lens_edit_right_pd"]');
    if (rpd) rpd.value = p['Right PD'] || '';
    var lpd = modal.querySelector('[name="lens_edit_left_pd"]');
    if (lpd) lpd.value = p['Left PD'] || '';

    updateModalTotal();
  }

  function openEditModal(configId) {
    ensureModal();
    activeConfigId = configId;
    var err = modal.querySelector('#lens-edit-error');
    err.classList.remove('is-visible');
    err.textContent = '';
    buildRxSelects();
    loadProduct(LENS_TYPE_HANDLE)
      .then(function (typeProduct) {
        renderOptionGrid('lens-edit-type', typeProduct.variants, 'lens_edit_type');
        return loadProduct(LENS_INDEX_HANDLE);
      })
      .then(function (indexProduct) {
        renderOptionGrid('lens-edit-index', indexProduct.variants, 'lens_edit_index');
        fillModalFromCache(configId);
      })
      .catch(function (e) {
        showModalError('Unable to load lens options: ' + e.message);
      });
    modal.classList.add('is-open');
  }

  function saveEdit() {
    if (!activeConfigId) return;
    var err = modal.querySelector('#lens-edit-error');
    err.classList.remove('is-visible');
    err.textContent = '';

    var indexRadio = modal.querySelector('input[name="lens_edit_index"]:checked');
    var typeRadio = modal.querySelector('input[name="lens_edit_type"]:checked');
    if (!indexRadio || !typeRadio) {
      showModalError('Please select a refractive index and a lens type.');
      return;
    }
    var pdTypeRadio = modal.querySelector('input[name="lens_edit_pd_type"]:checked');
    var pdType = pdTypeRadio ? pdTypeRadio.value : 'single';

    var read = function (name) {
      var el = modal.querySelector('[name="' + name + '"]');
      return el ? el.value.trim() : '';
    };
    var rx = {
      right: {
        sph: read('lens_edit_right_sph'),
        cyl: read('lens_edit_right_cyl'),
        axis: read('lens_edit_right_axis')
      },
      left: {
        sph: read('lens_edit_left_sph'),
        cyl: read('lens_edit_left_cyl'),
        axis: read('lens_edit_left_axis')
      }
    };
    var rxValues = [
      rx.right.sph, rx.right.cyl, rx.right.axis,
      rx.left.sph, rx.left.cyl, rx.left.axis
    ];
    if (rxValues.some(function (v) { return !v; })) {
      showModalError('Please complete the full prescription (SPH / CYL / AXIS for both eyes).');
      return;
    }
    var pd = '',
        pdRight = '',
        pdLeft = '';
    if (pdType === 'dual') {
      pdRight = read('lens_edit_right_pd');
      pdLeft = read('lens_edit_left_pd');
      if (!pdRight || !pdLeft) {
        showModalError('Please enter both Right PD and Left PD.');
        return;
      }
    } else {
      pd = read('lens_edit_pd');
      if (!pd) {
        showModalError('Please enter the Single PD value.');
        return;
      }
    }

    var all = (currentGroups.cart[activeConfigId] || [])
      .concat(currentGroups.minicart[activeConfigId] || []);
    var frame = all.find(isFrameRow);
    var frameData = frame && cartDataCache[frame.key];
    if (!frameData || !frameData.variant_id) {
      showModalError('Frame information missing. Please refresh the page and try again.');
      return;
    }

    var saveBtn = modal.querySelector('.lens-config-modal__save');
    saveBtn.classList.add('is-loading');
    saveBtn.disabled = true;
    var updates = {};
    var seen = {};
    all.forEach(function (r) {
      if (r.key && !seen[r.key]) {
        seen[r.key] = true;
        updates[r.key] = 0;
      }
    });

    var items = [
      {
        id: Number(frameData.variant_id),
        quantity: Number(frameData.quantity || 1),
        properties: {
          '_Lens Configuration': activeConfigId,
          'Prescription - Right SPH': rx.right.sph,
          'Prescription - Right CYL': rx.right.cyl,
          'Prescription - Right AXIS': rx.right.axis,
          'Prescription - Left SPH': rx.left.sph,
          'Prescription - Left CYL': rx.left.cyl,
          'Prescription - Left AXIS': rx.left.axis,
          'PD Type': pdType,
          'PD': pd,
          'Right PD': pdRight,
          'Left PD': pdLeft
        }
      },
      {
        id: Number(typeRadio.value),
        quantity: Number(frameData.quantity || 1),
        properties: { '_Lens Configuration': activeConfigId }
      },
      {
        id: Number(indexRadio.value),
        quantity: Number(frameData.quantity || 1),
        properties: { '_Lens Configuration': activeConfigId }
      }
    ];

    fetchJSON(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    })
      .then(function () {
        return fetchJSON(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: items,
            sections: getSectionIds(),
            sections_url: window.location.pathname
          })
        });
      })
      .then(function (state) {
        refreshCacheFromItems(state.items);
        applyCartState(state);
        modal.classList.remove('is-open');
        activeConfigId = null;
        reopenMiniCart();
      })
      .catch(function (e) {
        showModalError(e.message || 'Failed to update configuration.');
      })
      .finally(function () {
        saveBtn.classList.remove('is-loading');
        saveBtn.disabled = false;
      });
  }

  /* ----------------------------------------------------------
     Init
     ---------------------------------------------------------- */
  function observe() {
    // Watch the whole body: catches section swaps (AJAX re-renders of the
    // cart page + minicart), plus any other cart DOM changes. The rebuild
    // signature guard makes self-triggered rebuilds no-ops, so there is no
    // infinite loop.
    var obs = new MutationObserver(function () {
      scheduleRebuild();
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    setupLensDetailsToggle();
    setupGlobalDelegation();
    setupFrameOnlyRemove();
    loadCartData().then(function () {
      rebuild();
    });
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
